export type WorkMode = "wfo" | "wfh" | "field";
export type PayType = "fixed" | "fixed_incentive" | "incentive_only";
export type ExperienceType = "any" | "fresher" | "experienced";
export type Gender = "male" | "female" | "prefer_not_to_say";
export type WorkStatus = "working" | "fresher";
export type EnglishLevel = "none" | "basic" | "intermediate" | "advanced";
export type EnglishLevelRequired = "none" | "basic" | "good";
export type ContactPreference =
  | "candidates_call_recruiter"
  | "candidates_call_other"
  | "recruiter_contacts_first";
export type WhatsappAlert = "daily_digest" | "per_application_self" | "per_application_other";

export const WORK_MODE_OPTIONS = [
  { label: "Work From Office", value: "wfo" },
  { label: "Work From Home", value: "wfh" },
  { label: "Field Job", value: "field" },
] as const;

export const WORK_MODE_LABELS: Record<WorkMode, string> = {
  wfo: "Work From Office",
  wfh: "Work From Home",
  field: "Field Job",
};

export const PAY_TYPE_OPTIONS = [
  { label: "Fixed", value: "fixed", description: "Salary only" },
  { label: "Fixed + Incentive", value: "fixed_incentive", description: "Base plus variable" },
  { label: "Incentive Only", value: "incentive_only", description: "Commission / payout only" },
] as const;

export const PAY_TYPE_LABELS: Record<PayType, string> = {
  fixed: "Fixed",
  fixed_incentive: "Fixed + Incentive",
  incentive_only: "Incentive Only",
};

export type CtcPeriod = "monthly" | "yearly";

export const CTC_PERIOD_AMOUNT_LABEL: Record<CtcPeriod, string> = {
  monthly: "₹/month",
  yearly: "₹/year",
};

export const CTC_PERIOD_SWITCH_LABEL: Record<CtcPeriod, string> = {
  yearly: "Switch to yearly",
  monthly: "Switch to monthly",
};

export const EXPERIENCE_TYPE_OPTIONS = [
  { label: "Any", value: "any" },
  { label: "Freshers only", value: "fresher" },
  { label: "Experienced only", value: "experienced" },
] as const;

export const EXPERIENCE_TYPE_LABELS: Record<ExperienceType, string> = {
  any: "Any",
  fresher: "Freshers only",
  experienced: "Experienced only",
};

export const GENDER_OPTIONS = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
  { label: "Prefer not to say", value: "prefer_not_to_say" },
] as const;

export const GENDER_LABELS: Record<Gender, string> = {
  male: "Male",
  female: "Female",
  prefer_not_to_say: "Prefer not to say",
};

export const WORK_STATUS_OPTIONS = [
  { label: "I have work experience", value: "working" },
  { label: "Fresher / Student", value: "fresher" },
] as const;

export const RECRUITER_WORK_STATUS_OPTIONS = [
  { label: "Has work experience", value: "working" },
  { label: "Fresher / Student", value: "fresher" },
] as const;

export const ENGLISH_LEVEL_OPTIONS = [
  { value: "none", label: "No English", description: "I cannot speak or understand English" },
  { value: "basic", label: "Basic", description: "I can understand and speak basic sentences" },
  { value: "intermediate", label: "Intermediate", description: "I can have a conversation in English on some topics" },
  { value: "advanced", label: "Advanced", description: "I can do my entire job in English and speak fluently" },
] as const;

export const ENGLISH_LEVEL_REQUIRED_OPTIONS = [
  { label: "Not required", value: "" },
  { label: "Basic", value: "basic" },
  { label: "Good", value: "good" },
] as const;

export const ENGLISH_LEVEL_REQUIRED_LABELS: Record<EnglishLevelRequired | "none", string> = {
  none: "Not required",
  basic: "Basic English",
  good: "Good English",
};

export const CONTACT_PREFERENCE_LABELS: Record<ContactPreference, string> = {
  candidates_call_recruiter: "Candidates can call / WhatsApp me directly",
  candidates_call_other: "Forward calls to another team member",
  recruiter_contacts_first: "I will contact shortlisted candidates",
};

export const WHATSAPP_ALERT_LABELS: Record<WhatsappAlert, string> = {
  daily_digest: "One daily summary of all applications",
  per_application_self: "Alert me for every application — on my number",
  per_application_other: "Alert another recruiter for every application",
};

export const COMMON_INDIAN_LANGUAGES = [
  "Hindi",
  "Bengali",
  "Telugu",
  "Marathi",
  "Tamil",
  "Gujarati",
  "Kannada",
  "Malayalam",
  "Punjabi",
  "Odia",
  "Assamese",
  "Urdu",
  "Rajasthani",
  "Haryanvi",
  "Nepali",
  "Maithili",
] as const;

export const SEGMENT_SKILL_SUGGESTIONS: Record<string, string[]> = {
  bpo_ops: [
    "Voice Process",
    "Non-Voice Process",
    "Chat Support",
    "Data Entry",
    "Customer Service",
    "Telecalling",
    "Outbound Sales",
    "Inbound Support",
  ],
  default: [
    "Communication",
    "MS Office",
    "Data Entry",
    "Customer Service",
    "Problem Solving",
    "Time Management",
    "Teamwork",
    "English Typing",
  ],
};

export function workModeFromJob(workMode?: string | null, isRemote?: boolean): WorkMode {
  if (workMode === "wfo" || workMode === "wfh" || workMode === "field") return workMode;
  if (workMode === "remote" || isRemote) return "wfh";
  return "wfo";
}

export function keysWithValues(record: Record<string, unknown>): Set<string> {
  const keys = new Set<string>();
  for (const [key, value] of Object.entries(record)) {
    if (value == null) continue;
    if (typeof value === "string" && !value.trim()) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    keys.add(key);
  }
  return keys;
}
